import { Link } from 'react-router-dom';

// Ported from the App Router's RootLayout. The <html>/<body> shell now lives in
// index.html, and the five font CSS variables that RootLayout applied to <body>
// via next/font's generated classes are declared on :root in styles/fonts.css.

function OrbMark({ size = 24 }: { size?: number }) {
  return (
    <svg className="logo-orb" viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
      <defs>
        <linearGradient id="orb-base" x1="8" y1="6" x2="58" y2="58" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#96a4ea">
            <animate
              attributeName="stop-color"
              values="#96a4ea;#46688f;#2f6350;#96a4ea"
              dur="18s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="0.45" stopColor="#e9a3dd">
            <animate
              attributeName="stop-color"
              values="#e9a3dd;#d97a45;#7a8563;#e9a3dd"
              dur="18s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor="#f6c8a0">
            <animate
              attributeName="stop-color"
              values="#f6c8a0;#e59b5e;#c56f45;#f6c8a0"
              dur="18s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
        <radialGradient id="orb-blue" cx="0.26" cy="0.2" r="0.55">
          <stop offset="0" stopColor="#8b9ef2" stopOpacity="0.95">
            <animate
              attributeName="stop-color"
              values="#8b9ef2;#5a7fc0;#3c7c61;#8b9ef2"
              dur="18s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor="#8b9ef2" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="orb-pink" cx="0.64" cy="0.38" r="0.55">
          <stop offset="0" stopColor="#f7a8d8" stopOpacity="0.85">
            <animate
              attributeName="stop-color"
              values="#f7a8d8;#d97a45;#c56f45;#f7a8d8"
              dur="18s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor="#f7a8d8" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="orb-lav" cx="0.18" cy="0.82" r="0.5">
          <stop offset="0" stopColor="#b3a4ec" stopOpacity="0.9">
            <animate
              attributeName="stop-color"
              values="#b3a4ec;#6b89c8;#2f6350;#b3a4ec"
              dur="18s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor="#b3a4ec" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="orb-peach" cx="0.88" cy="0.78" r="0.55">
          <stop offset="0" stopColor="#f9d2a0" stopOpacity="0.95">
            <animate
              attributeName="stop-color"
              values="#f9d2a0;#e08a4e;#8a8a5f;#f9d2a0"
              dur="18s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor="#f9d2a0" stopOpacity="0" />
        </radialGradient>
        <filter id="orb-grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.14 0" />
          <feComposite operator="in" in2="SourceGraphic" />
        </filter>
        <clipPath id="orb-clip">
          <circle cx="32" cy="32" r="31" />
        </clipPath>
      </defs>
      <g clipPath="url(#orb-clip)">
        <rect width="64" height="64" fill="url(#orb-base)" />
        <rect className="orb-l1" width="64" height="64" fill="url(#orb-blue)" />
        <rect className="orb-l2" width="64" height="64" fill="url(#orb-pink)" />
        <rect className="orb-l3" width="64" height="64" fill="url(#orb-lav)" />
        <rect className="orb-l4" width="64" height="64" fill="url(#orb-peach)" />
        <rect width="64" height="64" fill="#ffffff" filter="url(#orb-grain)" />
      </g>
      <text
        x="32"
        y="44"
        textAnchor="middle"
        fontSize="35"
        fill="#ffffff"
        style={{ fontFamily: "var(--archivo-black), 'Arial Black', sans-serif" }}
      >
        %
      </text>
    </svg>
  );
}

function Logo() {
  return (
    <Link to="/" className="logo" aria-label="RateMyHYSA home">
      <span className="logo-line">
        <OrbMark />
        <span className="logo-ratemy">ratemy</span>
        <span className="logo-hysa">HYSA</span>
      </span>
    </Link>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <nav className="nav">
        <div className="wrap nav-inner">
          <Logo />
          <div className="nav-links">
            <Link to="/#rates" className="plain hide-mobile">
              Compare rates
            </Link>
            <Link to="/#how" className="plain hide-mobile">
              How it works
            </Link>
            <Link to="/blog" className="plain hide-mobile">
              Rate reports
            </Link>
            <a
              href="https://futuremoney.co/reserve"
              className="btn btn-primary btn-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get started
            </a>
          </div>
        </div>
      </nav>
      {children}
      <footer>
        <div className="wrap">
          <div className="foot-grid">
            <div>
              <Logo />
              <div style={{ marginTop: 10 }}>Data-driven. Transparent. Verified monthly.</div>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <Link to="/#rates">Compare rates</Link>
              <Link to="/blog">Rate reports</Link>
              <a href="https://futuremoney.co/reserve" target="_blank" rel="noopener noreferrer">
                FutureMoney Reserve
              </a>
            </div>
          </div>
          <p className="disclosure" id="disclosure">
            <strong>Advertiser disclosure.</strong> RateMyHYSA is an independent comparison site
            and free to use. We may earn a commission when you open an account through links on
            this site, including FutureMoney Reserve, with which the site&rsquo;s creator is
            affiliated. Compensation never determines our rankings: accounts are ordered by yield
            (tax-equivalent, where the tax treatment differs), and the top spot goes to whichever
            account earns it that month. FutureMoney Reserve is a brokerage product, not a bank
            account: it is protected by SIPC, not FDIC, and its yield varies with the underlying
            funds. Third-party rates are verified monthly against each institution&rsquo;s own
            website, with archived screenshots, and may have changed since; always confirm with
            the institution. Tax-equivalent figures are estimates based on 2026 single-filer
            federal and state marginal rates at an assumed income for your bracket, and are not
            tax or investment advice.
          </p>
        </div>
      </footer>
    </>
  );
}
